import { Button, Form, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowSummary, type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { formatDatetime } from '@/lib/formatDatetime';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

const APP_NAME_PATTERN = /^[a-z][a-z0-9-]{2,63}$/;

/** `YYYYMMDDHHmmss` suffix that create mode appends to auto-generated app names. */
const TIMESTAMP_SUFFIX = /-\d{14}$/;

type Props = {
  /** Workflow being cloned. Null = modal closed. */
  source: WorkflowSummary | null;
  /** All existing app names so we can warn before submit instead of via a 409 from the BE.
   *  Matched case-insensitively — see `existingSet` below. */
  existingAppNames: string[];
  onClose: () => void;
  onCloned: (saved: WorkflowTemplate) => void;
};

/**
 * Auto-suggest a "{base}-{YYYYMMDDHHmmss}" appName mirroring create mode: the
 * source's timestamp suffix (if any) is replaced with a fresh one. User can
 * edit before confirming.
 */
function suggestCloneAppName(srcAppName: string): string {
  const base = srcAppName.replace(TIMESTAMP_SUFFIX, '');
  const stamp = formatDatetime(new Date());
  // Keep within the 64-char app name limit: trim the base, never the timestamp.
  const trimmed = base.slice(0, 64 - stamp.length - 1).replace(/-+$/, '');
  return `${trimmed}-${stamp}`;
}

type FormValues = { newAppName: string };

export function CloneWorkflowModal({ source, existingAppNames, onClose, onCloned }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  // Lowercased on both sides: app name identity is case-insensitive on the server
  // (`AppNameRules.AreSame` — uniqueness on create, lookup by app name), so a name that
  // differs from an existing one only by case is a collision, not a free name. Stored
  // names are normally lowercase, but a row seeded/imported outside the domain factory
  // can carry mixed case — an exact-match check would clear it here and then take a 409.
  const existingSet = new Set(existingAppNames.map((n) => n.toLowerCase()));

  // Reset the suggested name every time we open for a different workflow.
  useEffect(() => {
    if (source) {
      form.setFieldsValue({ newAppName: suggestCloneAppName(source.appName) });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const close = () => {
    form.resetFields();
    onClose();
  };

  const handleConfirm = async () => {
    if (!source) return;
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const saved = await workflowsApi.clone(source.id, values.newAppName);
      message.success(t('workflow.list.cloned'));
      form.resetFields();
      onClose();
      onCloned(saved);
    } catch (err) {
      handleApiError(err, t, t('workflow.list.cloneFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BmoModal
      open={source !== null}
      onCancel={close}
      title={t('workflow.list.cloneTitle')}
      footer={[
        <Button key="cancel" onClick={close}>
          {t('common.cancel')}
        </Button>,
        <Button key="ok" type="primary" loading={submitting} onClick={handleConfirm}>
          {t('workflow.list.clone')}
        </Button>,
      ]}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item
          name="newAppName"
          label={t('workflow.fields.appNameSmartform')}
          extra={t('workflow.form.appNameHint')}
          style={{ marginBottom: 0 }}
          rules={[
            { required: true, message: t('workflow.form.appNameRequired') },
            { pattern: APP_NAME_PATTERN, message: t('workflow.form.appNameFormat') },
            {
              validator: (_, v) =>
                v && existingSet.has(v.toLowerCase())
                  ? Promise.reject(new Error(t('workflow.list.cloneAppNameTaken')))
                  : Promise.resolve(),
            },
          ]}
        >
          <Input placeholder="tfsa-account-opening-20260716120000" />
        </Form.Item>
      </Form>
    </BmoModal>
  );
}
